// Page wrapper for the Career CLI.
//
// Responsibilities:
//   - Detect mobile / touch-only / narrow viewports and redirect to /classic.
//     Phone keyboards make a fake terminal painful (no arrow keys, no Tab,
//     auto-suggest fights every keystroke), so we hard-pivot to the GUI.
//     The detection re-runs on `resize` too — so shrinking a desktop browser
//     into mobile dimensions also kicks in the redirect.
//   - Otherwise mount the <Terminal /> full-screen.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TmuxLayout from '../components/CareerCLI/TmuxLayout';
import BootSplash from '../components/CareerCLI/BootSplash';

/** Mobile heuristic: coarse pointer OR narrow viewport. */
function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false;
  const coarse = window.matchMedia?.('(pointer: coarse)').matches ?? false;
  const narrow = window.innerWidth < 768;
  return coarse || narrow;
}

// Short grace period so the visitor briefly sees the "redirecting…" message
// instead of teleporting. Was 2s — felt long enough that the real phone
// rendering of the tmux layout could flash for an instant before the
// redirect kicked in. 500ms removes that flicker without making the
// transition feel abrupt.
const REDIRECT_DELAY_MS = 500;

function CareerCLIPage() {
  const navigate = useNavigate();
  // `null` while we haven't decided yet — keeps SSR / first paint safe and
  // avoids flashing the terminal on mobile before we redirect.
  const [mobile, setMobile] = useState<boolean | null>(null);
  // Boot splash gate — show THANH-OS loader before TmuxLayout mounts.
  const [booted, setBooted] = useState<boolean>(false);
  // Pending navigate timer, kept in a ref so resize handlers can cancel it
  // when the viewport flips back to desktop dimensions.
  const redirectTimerRef = useRef<number | null>(null);

  useEffect(() => {
    function clearPendingRedirect() {
      if (redirectTimerRef.current !== null) {
        window.clearTimeout(redirectTimerRef.current);
        redirectTimerRef.current = null;
      }
    }

    function evaluate() {
      const m = isMobileDevice();
      setMobile(m);
      if (m) {
        // Schedule the redirect only once — re-evaluations on resize just
        // re-arm if a previous run already scheduled it.
        if (redirectTimerRef.current === null) {
          redirectTimerRef.current = window.setTimeout(() => {
            redirectTimerRef.current = null;
            navigate('/classic', { replace: true });
          }, REDIRECT_DELAY_MS);
        }
      } else {
        // Viewport widened (e.g. devtools toggled off) — cancel the redirect
        // so the visitor stays on the CLI page.
        clearPendingRedirect();
      }
    }

    evaluate();
    window.addEventListener('resize', evaluate);
    return () => {
      window.removeEventListener('resize', evaluate);
      clearPendingRedirect();
    };
  }, [navigate]);

  // Initial paint while detecting: render the dark surface so there's no
  // flash of white.
  if (mobile === null) {
    return <div className="fixed inset-0 bg-surface-dark" />;
  }

  if (mobile) {
    return (
      <div className="fixed inset-0 bg-surface-dark text-primary-400 font-mono flex items-center justify-center px-6 text-center text-sm sm:text-base">
        <div>
          <div className="mb-3 animate-pulse">[CLI mode is desktop-only —</div>
          <div className="mb-3 animate-pulse">taking you to Classic view...]</div>
          <div className="mt-6 text-gray-500 text-xs">
            redirecting…
          </div>
        </div>
      </div>
    );
  }

  // Desktop: play the boot splash, then mount the tmux layout.
  if (!booted) {
    return <BootSplash onComplete={() => setBooted(true)} />;
  }

  return <TmuxLayout />;
}

export default CareerCLIPage;
